import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { ILead, LeadPageState } from './typings'
import {
  bulkDeleteLeads,
  bulkReassignLeads,
  bulkReopenLeads,
  bulkUpdateLeadStatus,
  bulkUpdateNextActionDateLeads,
  createLead,
  deleteContact,
  deleteLead,
  getLeadDetail,
  getLeadList,
  getLeadValue,
  hasMultiServiceLocations,
  incrementLeadPaymentsCount,
  updateLeadInfo,
  updateLeadNextActionDate,
  updateLeadStatus,
  updateName,
  updateSaleTeamOwner,
} from './api'
import { defaultFilters, LeadFilterSet } from './filters'
import { setOrderBy, updateFilterState } from './helpers'
import { deserializeFilters } from '@user/components/Filters'
import { loadFilterPreferences } from '../userPreferences/api'
import { CardListViewMode, toast } from '@sales-center/components'
import { pageSizeOptions } from '@user/components/xGrid'
import {
  ENTITY_TYPES,
  IEntityContactDTO,
  IUser,
  updatePhoneValueSearch,
} from '@sales-center/common'
import { addEditEntityLocation, matchCreateNew } from '../entityLocations'
import { createOpportunity } from '../opportunity'
import {
  updateEntityClosingRequirements,
  updateScheduleRequirementSettings,
} from '../closingRequirements'

export const leadAdapter = createEntityAdapter<ILead>()

export const initialState = leadAdapter.getInitialState<LeadPageState>({
  page: 1,
  perPage: 10,
  count: 0,
  loading: true,
  shouldFetchList: false,
  filterSet: defaultFilters,
  mode: 'card',
  multipleServiceLocations: false,
})

export const leadSlice = createSlice({
  name: 'lead',
  initialState,
  reducers: {
    clearLeadDetail: (state) => {
      state.detail = undefined
    },
    setLeadPage: (state, { payload: page }: PayloadAction<number>) => {
      state.page = page > 0 ? page : 1
      state.shouldFetchList = true
    },
    setLeadsPerPage: (state, { payload: perPage }: PayloadAction<number>) => {
      state.page = 1
      state.perPage = perPage
      state.shouldFetchList = true
    },
    setLeadPageMode: (state, { payload }: PayloadAction<CardListViewMode>) => {
      state.mode = payload
      state.page = 1
      if (payload === 'list') {
        state.perPage =
          state.perPage > pageSizeOptions[0]
            ? state.perPage
            : pageSizeOptions[0]
      } else {
        state.perPage = 10
      }
      state.shouldFetchList = true

      setOrderBy(state, state.filterSet.orderBy)

      leadAdapter.removeAll(state)
    },
    setLeadFilters: (state, { payload }: PayloadAction<LeadFilterSet>) =>
      updateFilterState(state, payload),
    removeLeadFromList: (state, { payload: leadId }: PayloadAction<number>) => {
      leadAdapter.removeOne(state, leadId)
      state.count = Math.max(0, state.count - 1)
    },
    decrementLeadServicesCount: (
      state,
      { payload }: PayloadAction<{ leadId: number; count?: number }>,
    ) => {
      const { count = 1 } = payload
      const selectedLead = state.entities[payload.leadId]

      if (selectedLead) {
        const currentCount = selectedLead.servicesCount

        const update: Update<ILead> = {
          id: payload.leadId,
          changes: {
            servicesCount: currentCount ? Math.max(0, currentCount - count) : 0,
          },
        }
        leadAdapter.updateOne(state, update)
      }

      // Update lead detail
      if (!!state.detail && state.detail?.id == payload.leadId) {
        state.detail.servicesCount--
      }
    },
    incrementLeadServicesCount: (
      state,
      { payload }: PayloadAction<{ leadId: number }>,
    ) => {
      const selectedLead = state.entities[payload.leadId]

      if (selectedLead) {
        const currentCount = selectedLead.servicesCount

        const update: Update<ILead> = {
          id: payload.leadId,
          changes: {
            servicesCount: currentCount + 1,
          },
        }
        leadAdapter.updateOne(state, update)
      }

      // Update lead detail
      if (!!state.detail && state.detail?.id == payload.leadId) {
        state.detail.servicesCount++
      }
    },
    updateReferralIdOnCard: (
      state,
      {
        payload,
      }: PayloadAction<{ leadId: number; referralDiscountId: string }>,
    ) => {
      const update: Update<ILead> = {
        id: payload.leadId,
        changes: {
          referralDiscountId: payload.referralDiscountId,
        },
      }
      leadAdapter.updateOne(state, update)
    },
    updateLeadAgreementsCount: (
      state,
      {
        payload,
      }: PayloadAction<{
        action: 'decrement' | 'increment'
        leadId: number
      }>,
    ) => {
      const { action, leadId } = payload
      const selectedLead = state.entities[leadId]
      const value = action == 'increment' ? 1 : -1

      if (selectedLead) {
        const closingRequirements = selectedLead.closingRequirements

        const update: Update<ILead> = {
          id: payload.leadId,
          changes: {
            closingRequirements: {
              ...closingRequirements,
              agreementsCount: closingRequirements.agreementsCount + value,
            },
          },
        }
        leadAdapter.updateOne(state, update)
      }

      // Update lead detail
      if (!!state.detail && state.detail?.id == leadId) {
        state.detail.closingRequirements.agreementsCount += value
      }
    },
    updateDeletedLeadAgreementsCount: (
      state,
      {
        payload,
      }: PayloadAction<{
        leadId: number
        deletedAgreementsCount: number
      }>,
    ) => {
      const { leadId, deletedAgreementsCount } = payload
      const selectedLead = state.entities[leadId]

      if (selectedLead) {
        const closingRequirements = selectedLead.closingRequirements

        const update: Update<ILead> = {
          id: payload.leadId,
          changes: {
            closingRequirements: {
              ...closingRequirements,
              agreementsCount:
                closingRequirements.agreementsCount - deletedAgreementsCount,
            },
          },
        }
        leadAdapter.updateOne(state, update)
      }

      // Update lead detail
      if (!!state.detail && state.detail?.id == leadId) {
        state.detail.closingRequirements.agreementsCount -=
          deletedAgreementsCount
      }
    },
    setNextActionDate: (
      state,
      { payload }: PayloadAction<{ leadId: number; date: Date | null }>,
    ) => {
      const update: Update<ILead> = {
        id: payload.leadId,
        changes: {
          nextActionDate: payload.date,
        },
      }
      if (state.detail) {
        state.detail.nextActionDate = payload.date
      }
      leadAdapter.updateOne(state, update)
    },
    setAssigneeDate: (
      state,
      { payload }: PayloadAction<{ leadId: number; assignee: IUser }>,
    ) => {
      const update: Update<ILead> = {
        id: payload.leadId,
        changes: {
          assignee: payload.assignee,
        },
      }
      if (state.detail) {
        state.detail.assignee = payload.assignee
      }
      leadAdapter.updateOne(state, update)
    },
    bulkSetNextActionDate: (
      state,
      { payload }: PayloadAction<{ leadIds: number[]; date: Date | null }>,
    ) => {
      const updates: Update<ILead>[] = []
      payload.leadIds.map((leadId) =>
        updates.push({
          id: leadId,
          changes: {
            nextActionDate: payload.date,
          },
        }),
      )
      leadAdapter.updateMany(state, updates)
    },
  },
  extraReducers: (builder) => {
    //#region odata fetch
    builder.addCase(getLeadList.pending, (state) => {
      state.loading = true
      state.shouldFetchList = false
    })

    builder.addCase(getLeadList.rejected, (state) => {
      state.loading = false
      state.count = 0
      state.page = 1
      leadAdapter.removeAll(state)
    })

    builder.addCase(getLeadList.fulfilled, (state, { payload }) => {
      const { '@odata.count': count, value } = payload
      state.loading = false
      state.shouldFetchList = false
      if (count !== undefined) {
        state.count = count
      }

      // correct dates
      value.map((i) => {
        if (i.nextActionDate !== null)
          i.nextActionDate = new Date(i.nextActionDate)
        if (i.closedDate !== null) i.closedDate = new Date(i.closedDate)
        if (i.primaryContact.phone !== null)
          i.primaryContact.phone = updatePhoneValueSearch(
            i.primaryContact.phone,
          )
      })
      leadAdapter.setAll(state, value)
    })
    builder.addCase(
      hasMultiServiceLocations.fulfilled,
      (state, { payload }) => {
        const { value } = payload

        state.multipleServiceLocations = value.length > 0
      },
    )
    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const leadPref = payload.find((p) => p.type === 'lead')
      return updateFilterState(
        state,
        leadPref
          ? deserializeFilters<ILead, LeadFilterSet>(
              defaultFilters,
              leadPref.filter,
            )
          : defaultFilters,
      )
    })
    //#endregion

    builder.addCase(getLeadValue.fulfilled, (state, { payload }) => {
      const { value } = payload

      if (value.length > 0) {
        const {
          id,
          totalLeadValue,
          initialValue,
          recurringValue,
          billToDivisionId,
          billToLocationTypeId,
          serviceLocation,
          primaryContact,
          hasValidLocationAddress,
          hasLocationDetails,
          hasValidPropertyAddress,
        } = value[0]

        const update: Update<ILead> = {
          id,
          changes: {
            totalLeadValue,
            initialValue,
            recurringValue,
            billToDivisionId,
            billToLocationTypeId,
            serviceLocation,
            primaryContact,
            hasValidLocationAddress,
            hasLocationDetails,
            hasValidPropertyAddress,
          },
        }

        leadAdapter.updateOne(state, update)

        const { detail } = state
        if (!!detail && detail.id === id) {
          detail.initialValue = initialValue
          detail.recurringValue = recurringValue
          detail.totalLeadValue = totalLeadValue
        }
      }
    })

    builder.addCase(matchCreateNew.fulfilled, (state, action) => {
      const {
        meta: { arg },
      } = action

      if (arg.entityType === ENTITY_TYPES.Lead && !arg.detail) {
        state.shouldFetchList = true
      }
    })

    builder.addCase(
      addEditEntityLocation.fulfilled,
      (state, { meta, payload }) => {
        const {
          arg: { entityType, entityId },
        } = meta
        if (entityType === ENTITY_TYPES.Lead) {
          const lead = leadAdapter.getSelectors().selectById(state, entityId)
          if (lead?.primaryContactId === payload.contactId) {
            const contactName =
              payload.company !== null && payload.company.trim() !== ''
                ? payload.company
                : `${payload.firstName} ${payload.lastName}`

            const primaryContact: IEntityContactDTO = {
              externalLocationId: payload.externalLinkId ?? '',
              externalLocationCode: payload.locationCode ?? '',

              firstName: payload.firstName ?? '',
              lastName: payload.lastName ?? '',
              company: payload.company ?? '',
              contactName,

              emailAddresses: payload.emailAddresses,
              phone: payload.phoneNumber ?? '',
              phoneExt: payload.phoneExt ?? '',
              mobilePhone: '',

              address: payload.street1 ?? '',
              address2: payload.street2 ?? '',
              city: payload.city ?? '',
              state: payload.stateAbbreviation ?? '',
              postalCode: payload.postalCode ?? '',
              primaryEmailAddress: '',
            }

            leadAdapter.updateOne(state, {
              id: entityId,
              changes: {
                leadName: contactName,
                primaryContact,
              },
            })

            if (state.detail?.id === entityId) {
              state.detail.primaryContact = {
                ...state.detail.primaryContact,
                ...primaryContact,
              }
            }
          }
          if (state.detail) {
            const billLocationIndex =
              state.detail.locations.billToLocations.findIndex(
                (x) => x.id == payload.id,
              )
            if (billLocationIndex != -1)
              state.detail.locations.billToLocations[billLocationIndex] =
                payload
            else {
              const serviceLocationIndex =
                state.detail.locations.serviceLocations.findIndex(
                  (x) => x.id == payload.id,
                )
              if (serviceLocationIndex != -1)
                state.detail.locations.serviceLocations[serviceLocationIndex] =
                  payload
            }
          }
        }
      },
    )

    // ========== N E X T   A C T I O N   D A T E ==========
    builder.addCase(
      updateLeadNextActionDate.fulfilled,
      (state, { payload }) => {
        const update: Update<ILead> = {
          id: payload.leadId,
          changes: {
            nextActionDate: payload.nextActionDate,
          },
        }
        leadAdapter.updateOne(state, update)

        // Update lead detail
        if (!!state.detail && state.detail?.id == payload.leadId) {
          state.detail.nextActionDate = payload.nextActionDate
        }
      },
    )

    // ========== S A L E S  T E A M  ==========
    builder.addCase(updateSaleTeamOwner.fulfilled, (state, { payload }) => {
      const update: Update<ILead> = {
        id: payload.id,
        changes: {
          assigneeId: payload.assigneeId,
          assignee: payload.assignee,
          salesTeamId: payload.salesTeamId,
        },
      }
      leadAdapter.updateOne(state, update)

      // Update lead detail
      if (!!state.detail && state.detail?.id == payload.id) {
        state.detail.assigneeId = payload.assigneeId
        state.detail.assignee = payload.assignee
        state.detail.salesTeamId = payload.salesTeamId
        state.detail.salesTeam = payload.salesTeamName
      }
    })

    builder.addCase(updateLeadInfo.fulfilled, (state, { payload }) => {
      const update: Update<ILead> = {
        id: payload.id,
        changes: {
          assigneeId: payload.assigneeId,
          salesTeamId: payload.salesTeamId,
          locationTypeId: payload.locationTypeId,
          campaignId: payload.campaignId,
          salesFunnelId: payload.salesFunnelId,
        },
      }

      leadAdapter.updateOne(state, update)

      state.shouldFetchList = true

      // Update lead detail
      if (!!state.detail && state.detail?.id == payload.id) {
        state.detail.assigneeId = payload.assigneeId
        state.detail.salesTeamId = payload.salesTeamId
        state.detail.locationTypeId = payload.locationTypeId
        state.detail.campaignId = payload.campaignId
        state.detail.salesFunnelId = payload.salesFunnelId
      }
    })

    // ========== S T A T U S ==========
    builder.addCase(updateLeadStatus.fulfilled, (state, { payload }) => {
      const update: Update<ILead> = {
        id: payload.leadId,
        changes: {
          closedDate: payload.disqualifiedDate,
          closed: !!payload.disqualifiedDate,
          salesFunnelStageId: payload.salesFunnelStageId,
          age: payload.age ?? undefined,
          assigneeId: payload.owner ?? null,
        },
      }
      leadAdapter.updateOne(state, update)

      // Update lead detail
      if (state.detail?.id == payload.leadId) {
        state.detail.salesFunnelStageId = payload.salesFunnelStageId
        state.detail.salesFunnelStage = payload.salesFunnelStageName
        state.detail.age = payload.age!
        state.detail.closedDate = payload.disqualifiedDate
        state.detail.customDisqualifiedReason = payload.customReason
        state.detail.assigneeId = payload.owner || null
      }
    })

    builder.addCase(deleteLead.fulfilled, (state, { payload }) => {
      leadAdapter.removeOne(state, payload)
      state.count = state.count - 1
    })

    builder.addCase(createLead.fulfilled, (state) => {
      state.shouldFetchList = true
    })

    builder.addCase(getLeadDetail.fulfilled, (state, { payload }) => {
      const {
        id,
        referralSummary,
        externalLocationCode,
        externalLocationId,
        locations,
      } = payload

      const update: Update<ILead> = {
        id,
        changes: {
          referralDiscountCode: referralSummary?.referralDiscountCode,
          referralLocationId: referralSummary?.referralValue,
          referralDiscountId: referralSummary?.referralDiscountId,
          referralLocationCode: referralSummary?.referralLocationCode,
          externalLocationCode: externalLocationCode,
          externalLocationId: externalLocationId,
          locations: locations,
        },
      }

      leadAdapter.updateOne(state, update)

      state.detail = payload
      state.detail.primaryContact.altPhoneNumber = updatePhoneValueSearch(
        state.detail.primaryContact.altPhoneNumber ?? '',
      )
      state.detail.primaryContact.phoneNumber = updatePhoneValueSearch(
        state.detail.primaryContact.phoneNumber ?? '',
      )
      state.detail.billToContact.altPhoneNumber = updatePhoneValueSearch(
        state.detail.billToContact.altPhoneNumber ?? '',
      )
      state.detail.billToContact.phoneNumber = updatePhoneValueSearch(
        state.detail.billToContact.phoneNumber ?? '',
      )
      state.detail.serviceContact.altPhoneNumber = updatePhoneValueSearch(
        state.detail.serviceContact.altPhoneNumber ?? '',
      )
      state.detail.serviceContact.phoneNumber = updatePhoneValueSearch(
        state.detail.serviceContact.phoneNumber ?? '',
      )
      state.detail.secondaryContactsSummary.wwmContacts.map((c) => {
        c.contact.phoneNumber = updatePhoneValueSearch(
          c.contact.phoneNumber ?? '',
        )
        c.contact.altPhoneNumber = updatePhoneValueSearch(
          c.contact.altPhoneNumber ?? '',
        )
      })
    })

    builder.addCase(updateName.fulfilled, (state, { payload }) => {
      const { detail } = state
      const { id, contactId, firstName, lastName, company } = payload

      state.shouldFetchList = true

      if (detail && detail.id === id) {
        const { primaryContact, serviceContact, billToContact } = detail

        detail.firstName = firstName
        detail.lastName = lastName
        detail.company = company

        primaryContact.firstName = firstName
        primaryContact.lastName = lastName
        primaryContact.company = company

        if (serviceContact.id === contactId) {
          serviceContact.firstName = payload.firstName
          serviceContact.lastName = payload.lastName
          serviceContact.company = payload.company
        }

        if (billToContact.id === contactId) {
          billToContact.firstName = payload.firstName
          billToContact.lastName = payload.lastName
          billToContact.company = payload.company
        }
      }
    })

    builder.addCase(deleteContact.rejected, (_state, { payload }) => {
      toast.error(payload?.statusMessage)
    })
    builder.addCase(
      incrementLeadPaymentsCount.fulfilled,
      (state, { payload }) => {
        const leadId = payload
        const selectedLead = state.entities[leadId]

        if (selectedLead) {
          const closingRequirements = selectedLead.closingRequirements

          const update: Update<ILead> = {
            id: leadId,
            changes: {
              closingRequirements: {
                ...closingRequirements,
                paymentsCount: closingRequirements.paymentsCount + 1,
              },
            },
          }
          leadAdapter.updateOne(state, update)
        }

        // Update lead detail
        if (!!state.detail && state.detail?.id == leadId) {
          state.detail.closingRequirements.paymentsCount++
        }
      },
    )

    //#region bulk actions
    builder.addCase(bulkDeleteLeads.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    builder.addCase(bulkUpdateNextActionDateLeads.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    builder.addCase(bulkUpdateLeadStatus.fulfilled, (state, { payload }) => {
      state.shouldFetchList = payload.mode === 'commit'
    })
    builder.addCase(bulkReopenLeads.fulfilled, (state, { payload }) => {
      state.shouldFetchList = payload.mode === 'commit'
      if (
        payload.mode === 'commit' &&
        payload.model.alreadyOpenEntities?.length == 0
      )
        toast.success('Bulk update successfully applied')
    })
    builder.addCase(bulkReassignLeads.fulfilled, (state, { payload }) => {
      state.shouldFetchList = payload.mode === 'commit'
      if (payload.mode === 'commit' && payload.model.draftAgreementsUpdated) {
        toast.success('Information has been updated on any draft agreements.')
      }
    })
    //#endregion

    builder.addCase(createOpportunity.fulfilled, (state, { meta }) => {
      if (meta.arg.leadId) state.shouldFetchList = true
    })

    builder.addCase(updateScheduleRequirementSettings.fulfilled, (state, _) => {
      state.shouldFetchList = true
    })

    builder.addCase(
      updateEntityClosingRequirements.fulfilled,
      (state, { payload, meta }) => {
        const { entityId, entityType } = meta.arg

        if (entityType === ENTITY_TYPES.Lead) {
          const { services, servicesCount, closingRequirements } = payload

          leadAdapter.updateOne(state, {
            id: entityId,
            changes: {
              services,
              servicesCount,
              closingRequirements,
            },
          })

          if (state.detail && state.detail?.id === entityId) {
            state.detail.closingRequirements = {
              servicesCount,
              appointments: services,
              ...closingRequirements,
            }
          }
        }
      },
    )
  },
})

export default leadSlice.reducer

export const {
  clearLeadDetail,
  setLeadPage,
  setLeadsPerPage,
  setLeadPageMode,
  setLeadFilters,
  removeLeadFromList,
  decrementLeadServicesCount,
  incrementLeadServicesCount,
  updateLeadAgreementsCount,
  updateDeletedLeadAgreementsCount,
  updateReferralIdOnCard,
  setNextActionDate,
  bulkSetNextActionDate,
  setAssigneeDate,
} = leadSlice.actions

export const leadAdapterSelectors = leadAdapter.getSelectors<StoreData>(
  (state) => state.leadsState,
)

export const {
  selectById: selectLeadById,
  selectIds: selectLeadIds,
  selectEntities: selectLeadEntities,
  selectAll: selectAllLeads,
  selectTotal: selectTotalLeads,
} = leadAdapterSelectors

export const selectLeadDetail = (state: StoreData) => {
  return state.leadsState.detail
}

export const selectLeadPageState = (state: StoreData) => {
  const {
    leadsState: {
      page,
      perPage,
      count,
      loading,
      shouldFetchList,
      mode,
      multipleServiceLocations,
    },
  } = state

  return {
    page,
    pages: Math.ceil(count / perPage),
    perPage,
    count,
    loading,
    shouldFetchList,
    mode,
    multipleServiceLocations,
  }
}

export const selectLeadFilters = (state: StoreData) => {
  const {
    leadsState: { filterSet },
  } = state
  return { filterSet }
}
